import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { NewsletterEditorProvider } from "./context/NewsletterEditorContext";
import EditorMessageDialog from "./dialogs/EditorMessageDialog";
import { createEmptyDocument, normalizeDocument } from "./model/documentModel";
import { loadNewsletterDocument } from "../../../services/newsletterStorageService";
import { loadEditorSession } from "./utils/newsletterEditorSession";
import NewsletterEditorInner from "./NewsletterEditorInner";

const NewsletterEditor = ({ documentId }) => {
  const restoredSession = useRef(loadEditorSession());
  const [bootstrap, setBootstrap] = useState(() => {
    const session = restoredSession.current;
    if (session?.workspaceActive) {
      const sameDocument =
        !documentId || !session.savedObjectKey || session.savedObjectKey === documentId;
      if (sameDocument) {
        return {
          status: "ready",
          initialDocument: session.document,
          initialObjectKey: session.savedObjectKey,
          initialWorkspaceActive: true,
          initialDirty: session.dirty,
        };
      }
    }
    if (documentId) {
      return { status: "loading", initialDocument: createEmptyDocument(), initialObjectKey: documentId };
    }
    return {
      status: "ready",
      initialDocument: createEmptyDocument(),
      initialObjectKey: null,
      initialWorkspaceActive: false,
      initialDirty: false,
    };
  });
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (bootstrap.status !== "loading" || !documentId) {
      return;
    }
    let cancelled = false;
    loadNewsletterDocument(documentId)
      .then((doc) => {
        if (cancelled) {
          return;
        }
        setBootstrap({
          status: "ready",
          initialDocument: normalizeDocument(doc),
          initialObjectKey: documentId,
          initialWorkspaceActive: true,
          initialDirty: false,
        });
        setLoadError(null);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        setLoadError({
          title: "Caricamento newsletter non riuscito",
          message: err.message,
          detail: "Verifica che la bozza esista e che lo storage sia disponibile.",
        });
        setBootstrap((current) => ({ ...current, status: "error" }));
      });
    return () => {
      cancelled = true;
    };
  }, [bootstrap.status, documentId]);

  if (bootstrap.status === "loading") {
    return <Box sx={{ p: 3 }}>Caricamento editor...</Box>;
  }

  if (loadError) {
    return (
      <>
        <Box sx={{ p: 3 }}>Impossibile aprire la newsletter richiesta.</Box>
        <EditorMessageDialog
          open
          onClose={() => setLoadError(null)}
          title={loadError.title}
          message={loadError.message}
          detail={loadError.detail}
        />
      </>
    );
  }

  return (
    <NewsletterEditorProvider
      initialDocument={bootstrap.initialDocument}
      initialObjectKey={bootstrap.initialObjectKey}
      initialWorkspaceActive={bootstrap.initialWorkspaceActive}
      initialDirty={bootstrap.initialDirty}
    >
      <NewsletterEditorInner />
    </NewsletterEditorProvider>
  );
};

NewsletterEditor.propTypes = {
  documentId: PropTypes.string,
};

export default NewsletterEditor;
 